import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ChartBarIcon,
  ClockIcon,
  CursorArrowRaysIcon,
  EyeIcon,
  FireIcon,
  SparklesIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
  BoltIcon,
  CogIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'

const mainFeatures = [
  {
    name: 'AI-Powered Lead Discovery',
    description: 'Our advanced AI scans 1000+ subreddits 24/7, identifying perfect customers who are actively asking for solutions you provide.',
    icon: SparklesIcon,
    benefits: ['Finds 10x more leads than manual searching', 'Zero effort required - runs automatically', 'Discovers opportunities you would never find manually']
  },
  {
    name: 'Human-Like AI Comments',
    description: 'Generate authentic, mobile-style comments that sound exactly like real Reddit users. 0% ban rate guaranteed.',
    icon: ChatBubbleLeftRightIcon,
    benefits: ['Perfect Reddit tone and style', 'Never gets flagged as spam', 'Builds genuine relationships with potential customers']
  },
  {
    name: 'Smart Timing Optimization',
    description: 'AI determines the perfect time to comment for maximum visibility and engagement based on subreddit activity patterns.',
    icon: ClockIcon,
    benefits: ['3x better engagement rates', 'Comments appear when most users are active', 'Maximizes your visibility and response rates']
  },
  {
    name: 'Advanced Sentiment Analysis',
    description: 'Understand the emotional context of posts to engage only when users are most receptive to your solutions.',
    icon: EyeIcon,
    benefits: ['Higher conversion rates', 'Avoid negative interactions', 'Engage only high-quality prospects']
  },
  {
    name: 'Real-Time Monitoring',
    description: 'Get instant notifications the moment a perfect opportunity appears. Never miss a hot lead again.',
    icon: BoltIcon,
    benefits: ['Instant alerts via email/Slack/Discord', 'Be first to respond to opportunities', 'Strike while the iron is hot']
  },
  {
    name: 'ROI Analytics Dashboard',
    description: 'Track your performance with detailed analytics. See exactly which comments bring you customers.',
    icon: ChartBarIcon,
    benefits: ['Monitor cost per lead ($0.02 vs $5+ with ads)', 'Track conversion rates and ROI', 'Optimize your strategy with data']
  }
]

const additionalFeatures = [
  { name: 'Multi-Account Management', icon: CogIcon },
  { name: 'Keyword Optimization', icon: RocketLaunchIcon },
  { name: 'Ban Prevention System', icon: ShieldCheckIcon },
  { name: 'Custom Webhooks', icon: BoltIcon },
  { name: 'Team Collaboration', icon: ChatBubbleLeftRightIcon },
  { name: 'Trending Detection', icon: FireIcon },
  { name: 'Automated Scheduling', icon: ClockIcon },
  { name: 'Performance Tracking', icon: ChartBarIcon }
]

const testimonials = [
  {
    name: 'Sarah Chen',
    role: 'SaaS Founder',
    company: 'TaskFlow',
    content: 'Redd Miner generated 200+ qualified leads in our first month. Our conversion rate from Reddit is 3x higher than Google Ads. Game changer!',
    results: '200+ leads, 3x conversion rate'
  },
  {
    name: 'Mike Rodriguez',
    role: 'Digital Marketing Agency Owner',
    company: 'GrowthLab',
    content: 'We replaced our entire paid ads budget with Redd Miner. $50,000/month in new revenue from a $97/month tool. ROI is insane.',
    results: '$50k/month revenue from $97 investment'
  },
  {
    name: 'Jennifer Walsh',
    role: 'E-commerce Store Owner',
    company: 'FitGear Pro',
    content: 'Found 500+ customers in fitness subreddits. The AI comments are so natural, nobody realizes it\'s automated. My store revenue doubled.',
    results: '500+ customers, doubled revenue'
  }
]

const useCases = [
  {
    title: 'SaaS & Software Companies',
    description: 'Find developers and businesses discussing problems your software solves',
    examples: ['Generate demo requests', 'Drive trial signups', 'Build product awareness']
  },
  {
    title: 'Consultants & Agencies',
    description: 'Discover businesses actively seeking your expertise and services',
    examples: ['Book consultation calls', 'Generate qualified leads', 'Establish thought leadership']
  },
  {
    title: 'E-commerce & Retail',
    description: 'Connect with customers looking for products you sell',
    examples: ['Drive product sales', 'Build brand awareness', 'Increase website traffic']
  },
  {
    title: 'Course Creators & Coaches',
    description: 'Find people asking questions in your area of expertise',
    examples: ['Sell online courses', 'Book coaching sessions', 'Build email lists']
  }
]

const FeaturesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative isolate px-6 pt-14 lg:px-8">
        <div className="mx-auto max-w-4xl py-24 sm:py-32">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl"
            >
              Everything You Need to
              <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Dominate Reddit Marketing
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-6 text-xl leading-8 text-gray-600 max-w-3xl mx-auto"
            >
              Stop losing customers to competitors. Our AI-powered platform finds perfect prospects,
              engages them with human-like authenticity, and converts them into paying customers - all automatically.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-10"
            >
              <Link
                to="/signup"
                className="rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-4 text-lg font-semibold text-white shadow-sm hover:shadow-lg transition-all duration-200 hover:scale-105"
              >
                Start Finding Customers Today
              </Link>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Main Features */}
      <div className="py-24 sm:py-32 bg-gray-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center mb-16">
            <h2 className="text-base font-semibold leading-7 text-blue-600">Core Features</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              The Complete Reddit Marketing Automation Suite
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {mainFeatures.map((feature, index) => (
              <motion.div
                key={feature.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-4">
                  <feature.icon className="h-8 w-8 text-blue-600 mr-3" />
                  <h3 className="text-xl font-semibold text-gray-900">{feature.name}</h3>
                </div>
                <p className="text-gray-600 mb-6">{feature.description}</p>
                <ul className="space-y-2">
                  {feature.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center text-sm text-gray-700">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Additional Features Grid */}
      <div className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Plus 50+ Advanced Features
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Everything you need for professional Reddit marketing automation
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {additionalFeatures.map((feature, index) => (
              <motion.div
                key={feature.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-lg p-6 text-center hover:bg-gray-100 transition-colors"
              >
                <feature.icon className="h-8 w-8 text-blue-600 mx-auto mb-3" />
                <h3 className="font-medium text-gray-900">{feature.name}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Use Cases */}
      <div className="py-24 sm:py-32 bg-blue-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Perfect for Every Business Type
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              No matter what you sell, there are customers discussing it on Reddit right now
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={useCase.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg p-8 shadow-lg"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{useCase.title}</h3>
                <p className="text-gray-600 mb-6">{useCase.description}</p>
                <ul className="space-y-2">
                  {useCase.examples.map((example, i) => (
                    <li key={i} className="flex items-center text-sm text-gray-700">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                      {example}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Real Results from Real Customers
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Join thousands of businesses already succeeding with Redd Miner
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-lg p-8"
              >
                <p className="text-gray-700 mb-6 italic">"{testimonial.content}"</p>
                <div className="border-t pt-4">
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.role}</div>
                  <div className="text-sm text-blue-600 font-medium">{testimonial.company}</div>
                  <div className="text-sm text-green-600 font-semibold mt-2">{testimonial.results}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="px-6 py-24 sm:px-6 sm:py-32 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Ready to 10x Your Lead Generation?
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-blue-100">
              Join 2,000+ successful businesses using Redd Miner to find customers on Reddit.
              Start your free trial today - no credit card required.
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <Link
                to="/signup"
                className="rounded-lg bg-white px-8 py-4 text-lg font-semibold text-blue-600 shadow-sm hover:bg-blue-50 transition-all duration-200 hover:scale-105"
              >
                Start Free Trial Now
              </Link>
              <Link to="/pricing" className="text-lg font-semibold leading-6 text-white hover:text-blue-100">
                View Pricing <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FeaturesPage